/**
 * Unified Loading Spinner Component
 *
 * Provides consistent loading UI across the application with theme support
 * and optional layout variations.
 */

import React from 'react'

interface LoadingSpinnerProps {
  /** Loading message to display */
  message?: string
  /** Size of the spinner - defaults to 'medium' */
  size?: 'small' | 'medium' | 'large'
  /** Layout style - defaults to 'modern' (Business Impact style) */
  variant?: 'modern' | 'legacy'
  /** Whether to center the spinner in its container */
  centered?: boolean
  /** Whether to take full height of parent container */
  fullHeight?: boolean
  /** Custom className for additional styling */
  className?: string
}

// @purpose Canonical loading indicator with configurable size (small/medium/large), variant (modern/legacy), centering, and full-height layout. Use anywhere data is in-flight instead of inline spinners.
const LoadingSpinner = ({
  message = 'Loading...',
  size = 'medium',
  variant = 'modern',
  centered = true,
  fullHeight = false,
  className = '',
}: LoadingSpinnerProps): React.JSX.Element => {
  // Size configurations
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
  }

  const textSizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base',
  }

  // Variant styles
  const spinnerClasses = {
    modern: `inline-block animate-spin rounded-full ${sizeClasses[size]} border-b-2 border-gray-900 dark:border-gray-100`,
    legacy: `animate-spin rounded-full ${sizeClasses[size]} border-b-2 border-blue-500`,
  }

  const containerClasses = centered
    ? `flex items-center justify-center ${fullHeight ? 'h-full' : ''}`
    : ''

  const textClasses =
    variant === 'modern'
      ? `mt-2 ${textSizeClasses[size]} text-gray-600 dark:text-gray-400`
      : `mt-2 ${textSizeClasses[size]} text-gray-500`

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="flex flex-col justify-center items-center">
        <div className={spinnerClasses[variant]} />
        {message && <p className={textClasses}>{message}</p>}
      </div>
    </div>
  )
}

export default LoadingSpinner
